import {
	Box,
	Button,
	Heading,
	Icons,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui/base"
import { Dialog, useDialog } from "@sembark-travel/ui/dialog"
import {
	FieldArray,
	Form,
	arrayMutators,
	withServerErrors,
	GetFieldValue,
} from "@sembark-travel/ui/form"
import React from "react"
import { TDateswiseTransportsAndActivitiesInputFieldValue } from "./utils"

export type ServiceDisplay = {
	id: string
	type: "transport_service" | "travel_activity"
	displayLabel: string
	transportations?: { name: string; quantity: number }[]
	timeStamps?: { slot?: string; duration?: string }
	pax?: { name: string; quantity: number }[]
}

// Raw service type from the form
type ServiceItem = Required<
	TDateswiseTransportsAndActivitiesInputFieldValue[number]
>["services"][number]

type TReorderDatewiseTransportAndActivitiesProps = {
	services: ServiceItem[]
	onChange: (reordered: ServiceItem[]) => void
	children: (props: { reOrder: () => void }) => React.ReactNode
}

// Helper function to convert ServiceItem to ServiceDisplay
function convertToServiceDisplay(s: ServiceItem): ServiceDisplay {
	return {
		id:
			s.type === "transport_service"
				? String(s.service?.transport_service?.id + "_" + s.__id || s.__id)
				: String(s.service?.activity?.id + "_" + s.__id || s.__id),
		type: s.type,
		displayLabel:
			s.type === "transport_service"
				? `${s.service?.transport_service_location?.name ?? s.service?.transport_service_location?.short_name ?? "N/A"} - ${s.service?.transport_service?.name ?? s.service?.transport_service?.service_display_name ?? "N/A"}`
				: `${s.service?.activity?.name ?? "N/A"} - ${s.service?.ticket_type?.name ?? "N/A"}`,
		...(s.type === "transport_service"
			? {
					transportations:
						s.service.cabs?.map((cab) => ({
							name: cab.cab_type?.display_name || cab.cab_type?.name || "N/A",
							quantity: cab.no_of_cabs || 0,
						})) ?? [],
				}
			: {
					pax:
						s.service?.ticket_tourist_configurations?.map((curr) => ({
							name: curr.configuration?.name || "N/A",
							quantity: curr.quantity || 0,
						})) ?? [],
					timeStamps: {
						slot: s.service?.slot || undefined,
						duration: s.service?.duration?.name || undefined,
					},
				}),
	}
}

function ServiceInfoCard({ service }: { service: ServiceDisplay }) {
	return (
		<Stack>
			<Inline gap="2" alignItems="center">
				<Box>
					{service.type === "transport_service" ? (
						<Icons.Taxi size="5" />
					) : (
						<Icons.Ticket size="5" />
					)}
				</Box>
				<Text>{service.displayLabel}</Text>
			</Inline>
			{service.transportations?.length ? (
				<Box paddingLeft="8">
					{service.transportations.map((t) => (
						<Text key={t.name} as="li" color="muted" fontSize="sm">
							{t.quantity ?? "N/A"} {t.name ?? "N/A"}
						</Text>
					))}
				</Box>
			) : null}
			{service.timeStamps &&
			(service.timeStamps.slot || service.timeStamps.duration) ? (
				<Box paddingLeft="8">
					<Text
						key={service.timeStamps.slot}
						as="li"
						color="muted"
						fontSize="sm"
					>
						{service.timeStamps.slot ?? "N/A"}{" "}
						{service.timeStamps.duration
							? `(${service.timeStamps.duration})`
							: "N/A"}
					</Text>
				</Box>
			) : null}

			{service.pax?.length ? (
				<Box paddingLeft="8">
					<Text color="muted" fontSize="sm" as="li">
						{service.pax
							.map((p) => `${p.quantity ?? "N/A"} ${p.name ?? "N/A"}`)
							.join(", ")}
					</Text>
				</Box>
			) : null}
		</Stack>
	)
}

/* ----- UI rows ----- */

function AvailableServiceRow({
	service,
	disabled,
	onSelect,
}: {
	service: ServiceDisplay
	disabled: boolean
	onSelect: () => void
}) {
	return (
		<Inline
			opacity={disabled ? "20" : "100"}
			gap="2"
			alignItems="center"
			transition="1"
		>
			<Box flex="1" minWidth="0">
				<Box paddingX="4" paddingY="2" borderWidth="1" rounded="md">
					<Inline gap="4" alignItems="center" justifyContent="between">
						<ServiceInfoCard service={service} />
						<Button size="sm" disabled={disabled} onClick={onSelect}>
							<Icons.ArrowLeft rotate="180" />
						</Button>
					</Inline>
				</Box>
			</Box>
		</Inline>
	)
}

function OrderedServiceRow({
	value,
	index,
	onRemove,
}: {
	value: ServiceDisplay
	index: number
	onRemove: () => void
}) {
	return (
		<Inline gap="2" alignItems="center">
			<Stack
				size="6"
				alignItems="center"
				justifyContent="center"
				borderWidth="1"
				rounded="full"
				bgColor="success_emphasis"
				color="on_emphasis"
			>
				<Text fontWeight="semibold">{index + 1}</Text>
			</Stack>
			<Box flex="1" minWidth="0">
				<Box paddingX="4" paddingY="2" borderWidth="1" rounded="md">
					<Inline gap="4" alignItems="center" justifyContent="between">
						<ServiceInfoCard service={value} />
						<Button size="sm" onClick={onRemove}>
							<Icons.Cancel />
						</Button>
					</Inline>
				</Box>
			</Box>
		</Inline>
	)
}

/* ----- Main component ----- */

export function ReorderDatewiseTransportAndActivities({
	services,
	onChange,
	children,
}: TReorderDatewiseTransportAndActivitiesProps) {
	const [isOpen, open, close] = useDialog()

	// Convert services to ServiceDisplay only when needed
	const servicesWithLabels = React.useMemo(() => {
		return services.map(convertToServiceDisplay)
	}, [services])
	return (
		<>
			{children?.({ reOrder: open })}
			<Dialog open={isOpen} onClose={close} title="Re-Order Services">
				<Form<{ ordered_services: ServiceDisplay[] }>
					key={isOpen ? "reorder-form" : "reorder-form-closed"}
					initialValues={{ ordered_services: [] }}
					onSubmit={withServerErrors(async ({ ordered_services }) => {
						// Index-based reordering logic - easily testable
						// This function can be extracted and unit tested independently
						const reorderedServices: ServiceItem[] = []

						// Map ordered services back to original ServiceItem objects using index
						for (const serviceDisplay of ordered_services || []) {
							// Find the original service by matching the display properties
							const originalIndex = servicesWithLabels.findIndex(
								(s) => s.id === serviceDisplay.id
							)
							if (originalIndex !== -1) {
								reorderedServices.push(services[originalIndex])
							}
						}

						// Add any remaining services that weren't selected (shouldn't happen in normal flow)
						const selectedIndices = new Set(
							ordered_services?.map((s) => {
								const index = servicesWithLabels.findIndex(
									(orig) => orig.id === s.id
								)
								return index
							}) || []
						)

						const unselectedServices = services.filter(
							(_, index) => !selectedIndices.has(index)
						)
						const allServices = [...reorderedServices, ...unselectedServices]

						close()
						onChange(allServices)
					})}
					mutators={{ ...arrayMutators }}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, submitting }) => (
						<form onSubmit={handleSubmit}>
							<Dialog.Body>
								<FieldArray<ServiceDisplay>
									name="ordered_services"
									subscription={{ value: true }}
								>
									{({ fields }) => {
										const orderedIds = new Set(
											fields.value?.map((s: ServiceDisplay) => s.id) || []
										)

										return (
											<Inline gap="4" collapseBelow="sm">
												{/* Left side: Available services */}
												<Box flex="1">
													<Stack gap="6">
														<Stack gap="1">
															<Heading fontSize="md">
																Select services in desired order
															</Heading>
															<Text fontSize="sm" color="muted">
																They’ll appear in the order you click.
															</Text>
														</Stack>

														{servicesWithLabels.map((service) => (
															<Box key={service.id}>
																<AvailableServiceRow
																	service={service}
																	disabled={orderedIds.has(service.id)}
																	onSelect={() => fields.push(service)}
																/>
															</Box>
														))}
													</Stack>
												</Box>

												{/* Divider */}
												<Box alignSelf="stretch" borderWidth="1" />

												{/* Right side: Ordered services */}
												<Box flex="1">
													<Stack gap="6">
														<Stack gap="1">
															<Heading fontSize="md">Ordered Services</Heading>
															<Text fontSize="sm" color="muted">
																Remove and re-add to change order.
															</Text>
														</Stack>

														{fields.map((name, index) => (
															<Box key={name}>
																<GetFieldValue<ServiceDisplay> name={name}>
																	{({ value }) => (
																		<OrderedServiceRow
																			value={value}
																			index={index}
																			onRemove={() => fields.remove(index)}
																		/>
																	)}
																</GetFieldValue>
															</Box>
														))}
													</Stack>
												</Box>
											</Inline>
										)
									}}
								</FieldArray>
							</Dialog.Body>

							<Dialog.Footer>
								<Button type="submit" disabled={submitting}>
									Save Order
								</Button>
								<Button disabled={submitting} onClick={close}>
									Cancel
								</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}
